import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  TextField
} from "@mui/material";

const cellSx = {
  backgroundColor: "rgba(47, 53, 71, 0.9)",
  border: "1.5px solid #fff",
  color: "white",
  padding: "10px 8px"
};

const numberSx = {
  ...cellSx,
  textAlign: "left",
  fontWeight: 600,
};

const JustifiedWithdrawalInternalTable = ({
  salary = 0,
  christmasBonus = 0,
  accruedVacation = 0,
  proportionalVacation = 0,
  internalSeverance = 0,
  notice = 0,
  total = 0,
  onValuesChange // optional callback to notify parent on edits
}) => {
  // Editable values stored as strings for controlled inputs
  const [editableAccruedVacation, setEditableAccruedVacation] = useState(String(accruedVacation));
  const [editableProportionalVacation, setEditableProportionalVacation] = useState(String(proportionalVacation));
  const [editableNotice, setEditableNotice] = useState(String(notice));
  const [editableInternalSeverance, setEditableInternalSeverance] = useState(String(internalSeverance));

  // Sync prop changes to state
  useEffect(() => setEditableAccruedVacation(String(accruedVacation)), [accruedVacation]);
  useEffect(() => setEditableProportionalVacation(String(proportionalVacation)), [proportionalVacation]);
  useEffect(() => setEditableNotice(String(notice)), [notice]);
  useEffect(() => setEditableInternalSeverance(String(internalSeverance)), [internalSeverance]);

  // Input change handler with numeric validation (empty allowed)
  const handleInputChange = (setter, name) => (event) => {
    const val = event.target.value;
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      setter(val);
      // Notify parent on change
      if (typeof onValuesChange === "function") {
        onValuesChange({
          accruedVacation: name === "accruedVacation" ? Number(val) || 0 : Number(editableAccruedVacation) || 0,
          proportionalVacation: name === "proportionalVacation" ? Number(val) || 0 : Number(editableProportionalVacation) || 0,
          notice: name === "notice" ? Number(val) || 0 : Number(editableNotice) || 0,
          internalSeverance: name === "internalSeverance" ? Number(val) || 0 : Number(editableInternalSeverance) || 0
        });
      }
    }
  };

  // Calculate total dynamically including editable internal severance
  const correctedTotal =
    (Number(salary) || 0) +
    (Number(christmasBonus) || 0) +
    (Number(editableAccruedVacation) || 0) +
    (Number(editableProportionalVacation) || 0) +
    (Number(editableNotice) || 0) +
    (Number(editableInternalSeverance) || 0);

  // Helper for formatting numbers with 2 decimals
  const formatNum = (num) =>
    num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const rows = [
    ["Salario Real", formatNum(Number(salary)), "Based on last full or partial month worked"],
    ["Aguinaldo", formatNum(Number(christmasBonus)), "Total o Proporcional"],
    [
      "Vacaciones Causadas",
      <TextField
        value={editableAccruedVacation}
        onChange={handleInputChange(setEditableAccruedVacation, "accruedVacation")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "Si tienes más de 1 año de servicio"
    ],
    [
      "Vacaciones proporcionales",
      <TextField
        value={editableProportionalVacation}
        onChange={handleInputChange(setEditableProportionalVacation, "proportionalVacation")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "Sólo días/meses si no se cumple un año"
    ],
    [
      "Preaviso",
      <TextField
        value={editableNotice}
        onChange={handleInputChange(setEditableNotice, "notice")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "si corresponde"
    ],
    [
      "Indemnización interna",
      <TextField
        value={editableInternalSeverance}
        onChange={handleInputChange(setEditableInternalSeverance, "internalSeverance")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "cálculo de indemnización interna"
    ],
  ];

  return (
    <Box mt={4}>
      <Typography variant="h6" mb={2} fontWeight="bold" color="white">
        Retiro Justificado Art. 84 – Interno
      </Typography>
      <TableContainer component={Paper} sx={{ background: "rgba(47,53,71,0.9)" }}>
        <Table>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={cellSx}>{row[0]}</TableCell>
                <TableCell sx={numberSx}>{row[1]}</TableCell>
                <TableCell sx={cellSx}>{row[2]}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Total to Pay</TableCell>
              <TableCell
                colSpan={2}
                sx={{
                  ...numberSx,
                  background: "rgba(34,189,110,0.15)",
                  color: "#3afa60",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: 20
                }}
              >
                {correctedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default JustifiedWithdrawalInternalTable;
